/**
 * Fair Price (JJ Simon) indicator store
 *
 * Frontend state for the Rust engine's fair-price strategy:
 * - Anchors (9:29 RTH body box, 6PM/8PM/2PM opens, 8:29 pre-news box)
 * - Phase state (continuation / reversion / dead) per session open
 * - Armed/at-fair-price distance gate
 * - A+/A signals (BOS-and-close / displacement toward the anchor)
 *
 * Contract (served by utt-engine):
 * - REST  GET {getApiBaseUrl()}/fair-price?symbol=...        -> { success, state, signals }
 * - REST  GET/POST {getApiBaseUrl()}/fair-price-settings     -> FairPriceSettings
 * - Socket "fair-price:update"  -> FairPriceUpdate (the state object)
 * - Socket "fair-price:signal"  -> FairPriceSignal (one signal)
 * - Socket "fair-price:signal-update" -> FairPriceSignal (lifecycle upsert:
 *   the full updated signal object, merged by id via the same path as
 *   "fair-price:signal")
 *
 * The engine may not be deployed yet — REST 404s / network errors are
 * tolerated with a single console.warn (no spam).
 *
 * ZUSTAND RULE (repo-wide): any selector returning a NEW array/object MUST be
 * wrapped in useShallow (see autoBotStore.useEnabledBots) — an unwrapped
 * inline filter selector caused a React #185 production crash.
 */

import { create } from 'zustand';
import { useShallow } from 'zustand/react/shallow';
import { wsService } from '../services/WebSocketService';
import { getApiBaseUrl } from '../config/ports';
import { normalizeToBase } from '../utils/symbolUtils';

// ============================================================================
// Types (mirror the utt-engine contract exactly)
// ============================================================================

export type FairPriceAnchorKind =
  | 'rth929'
  | 'open6pm'
  | 'open8pm'
  | 'open2pm'
  | 'prenews'
  | 'migrated';

export type FairPricePhaseMode = 'continuation' | 'reversion' | 'dead';

/** Signal lifecycle status. A missing status means the signal is still live. */
export type FairPriceSignalStatus = 'live' | 'win' | 'stop' | 'expired';

/** Rolling outcome stats on the "fair-price:update" payload (optional: older engines omit it). */
export interface FairPriceStats {
  wins: number;
  stops: number;
  expired: number;
  winRatePct: number;
}

export interface FairPriceAnchor {
  kind: FairPriceAnchorKind;
  /** Anchor reference price (body mid / session open). */
  price: number;
  /** Top of the anchor body box (== price for pure open anchors). */
  priceHigh: number;
  /** Bottom of the anchor body box (== price for pure open anchors). */
  priceLow: number;
  /** When the anchor was set, ms epoch. */
  setAt: number;
  active: boolean;
}

export interface FairPricePhase {
  /** Which session open this phase belongs to (e.g. "rth", "6pm"), null when none. */
  session: string | null;
  mode: FairPricePhaseMode;
  /** Session open time, ms epoch. */
  sessionOpenAt: number;
}

/** The "fair-price:update" payload / REST `state` object. */
export interface FairPriceUpdate {
  symbol: string;
  anchors: FairPriceAnchor[];
  phase: FairPricePhase;
  /** true when |price - anchor| >= TP distance (tradeable). */
  armed: boolean;
  /** Live |price - anchor| distance in points. */
  distance: number;
  /**
   * Kind of the anchor currently driving the phase/armed logic (null when
   * none). Optional: older engine builds don't send it.
   */
  activeAnchorKind?: FairPriceAnchorKind | null;
  /**
   * true when the ATH guard is active (shorts suppressed near all-time highs).
   * Optional: older engine builds don't send it.
   */
  athGuardActive?: boolean;
  /**
   * Count of currently-live (unresolved) signals. Optional: older engine
   * builds don't send it.
   */
  liveSignals?: number;
  /**
   * Rolling win/stop/expired outcome stats. Optional: older engine builds
   * don't send it.
   */
  stats?: FairPriceStats;
}

export interface FairPriceSignal {
  id: string;
  symbol: string;
  /** Signal bar time, ms epoch. */
  timestamp: number;
  grade: 'A+' | 'A';
  direction: 'long' | 'short';
  entry: number;
  anchorKind: FairPriceAnchorKind;
  anchorPrice: number;
  slPoints: number;
  tpPoints: number;
  /** true when the opening candle was big -> halve contracts (50/76 template). */
  halfSize: boolean;
  /**
   * |price - anchor| distance in points at signal time. Optional: the engine
   * may not include it on "fair-price:signal" yet — render only when present.
   */
  distance?: number;
  // --- Lifecycle fields (all optional: older engine payloads omit them) ---
  /** Lifecycle status; treat missing as "live". */
  status?: FairPriceSignalStatus;
  /** When the signal resolved (win/stop/expired), ms epoch. */
  resolvedAt?: number;
  /** Price at resolution. */
  resolvedPrice?: number;
  /** Max favorable excursion in points while live. */
  maxFavorablePts?: number;
  /** Max adverse excursion in points while live. */
  maxAdversePts?: number;
}

export interface FairPriceSettings {
  enabled: boolean;
  tpPoints: number;
  slPoints: number;
  bigCandlePts: number;
  swingLookback: number;
  sessionsRth: boolean;
  sessionsEvening: boolean;
  sessionsPm: boolean;
  athGuard: boolean;
  // Anchor-migration detection (impulse move -> consolidation -> new anchor)
  migrationEnabled: boolean;
  /** Volume multiple vs average that qualifies the impulse leg. */
  migrationVolMult: number;
  /** Minimum impulse move in points. */
  migrationMovePts: number;
  /** Bars the impulse must complete within. */
  migrationWindowBars: number;
  /** Consolidation bars required after the impulse. */
  migrationConsolBars: number;
  /** Max consolidation range in points. */
  migrationConsolRangePts: number;
}

// ============================================================================
// Constants / helpers
// ============================================================================

const ENABLED_STORAGE_KEY = 'fairPrice.enabled';
const MAX_SIGNALS = 100;

export const DEFAULT_FAIR_PRICE_SETTINGS: FairPriceSettings = {
  enabled: false,
  tpPoints: 38,
  slPoints: 25,
  bigCandlePts: 25,
  swingLookback: 2,
  sessionsRth: true,
  sessionsEvening: true,
  sessionsPm: true,
  athGuard: true,
  migrationEnabled: true,
  migrationVolMult: 2,
  migrationMovePts: 20,
  migrationWindowBars: 10,
  migrationConsolBars: 5,
  migrationConsolRangePts: 10,
};

function loadPersistedEnabled(): boolean {
  try {
    return localStorage.getItem(ENABLED_STORAGE_KEY) === 'true';
  } catch {
    return false; // SSR / storage blocked
  }
}

function persistEnabled(enabled: boolean): void {
  try {
    localStorage.setItem(ENABLED_STORAGE_KEY, String(enabled));
  } catch {
    // storage blocked — non-fatal
  }
}

// The engine may not be deployed yet: 404s / connection refusals are expected.
// Warn exactly ONCE for the whole session instead of spamming the console.
let warnedUnavailable = false;
function warnUnavailableOnce(what: string): void {
  if (warnedUnavailable) return;
  warnedUnavailable = true;
  console.warn(
    `[FairPrice] ${what} unavailable — engine endpoints not deployed yet? (further warnings suppressed)`
  );
}

/** Insert/replace a signal by id, keep sorted by timestamp, cap at MAX_SIGNALS (newest kept). */
function mergeSignal(existing: FairPriceSignal[], signal: FairPriceSignal): FairPriceSignal[] {
  const idx = existing.findIndex((s) => s.id === signal.id);
  const next = idx === -1 ? [...existing, signal] : existing.map((s, i) => (i === idx ? signal : s));
  next.sort((a, b) => a.timestamp - b.timestamp);
  return next.length > MAX_SIGNALS ? next.slice(next.length - MAX_SIGNALS) : next;
}

// ============================================================================
// Store
// ============================================================================

interface FairPriceStoreState {
  /** Indicator visibility toggle (persisted to localStorage 'fairPrice.enabled'). */
  enabled: boolean;
  /**
   * Latest engine state from ANY symbol, null before the first update.
   *
   * The engine streams updates for every watched symbol, so this flips between
   * them — anything rendered against a specific chart MUST read
   * `statesBySymbol` instead, or it will show ES numbers on an MNQ chart.
   */
  state: FairPriceUpdate | null;
  /** Latest engine state per BASE symbol (MNQ, ES, ...). */
  statesBySymbol: Record<string, FairPriceUpdate>;
  /** A+/A signals, sorted by timestamp, capped at 100. */
  signals: FairPriceSignal[];
  /** Strategy settings mirrored from GET /fair-price-settings. */
  settings: FairPriceSettings;

  setEnabled: (enabled: boolean) => void;
  applyUpdate: (update: FairPriceUpdate) => void;
  addSignal: (signal: FairPriceSignal) => void;
  clearSignals: () => void;

  /** Register the two socket listeners (idempotent; retries until the socket exists). */
  initialize: () => void;
  /** Remove the socket listeners registered by initialize(). */
  teardown: () => void;

  fetchState: (symbol: string) => Promise<void>;
  fetchSettings: () => Promise<void>;
  updateSettings: (partial: Partial<FairPriceSettings>) => Promise<void>;
}

// --- Socket listener plumbing (module scope so teardown can off() the exact fns) ---

let handlersRegistered = false;
let registerRetryTimer: ReturnType<typeof setInterval> | null = null;

const onFairPriceUpdate = (update: FairPriceUpdate): void => {
  if (!update || !Array.isArray(update.anchors) || !update.phase) return;
  useFairPriceStore.getState().applyUpdate(update);
};

const onFairPriceSignal = (signal: FairPriceSignal): void => {
  if (!signal || signal.id == null || typeof signal.timestamp !== 'number') return;
  useFairPriceStore.getState().addSignal(signal);
};

function stopRegisterRetry(): void {
  if (registerRetryTimer !== null) {
    clearInterval(registerRetryTimer);
    registerRetryTimer = null;
  }
}

function tryRegisterHandlers(): boolean {
  // wsService.on() is a no-op (warn) while the socket is uninitialized; only
  // register once we know the socket exists (isConnected implies it does).
  if (!wsService.isConnected()) return false;
  wsService.on('fair-price:update', onFairPriceUpdate);
  wsService.on('fair-price:signal', onFairPriceSignal);
  // Lifecycle updates carry the full updated signal — same upsert path.
  wsService.on('fair-price:signal-update', onFairPriceSignal);
  handlersRegistered = true;
  return true;
}

export const useFairPriceStore = create<FairPriceStoreState>((set, get) => ({
  enabled: loadPersistedEnabled(),
  state: null,
  statesBySymbol: {},
  signals: [],
  settings: { ...DEFAULT_FAIR_PRICE_SETTINGS },

  setEnabled: (enabled) => {
    persistEnabled(enabled);
    set({ enabled });
  },

  applyUpdate: (update) =>
    set((s) => ({
      state: update,
      statesBySymbol: { ...s.statesBySymbol, [normalizeToBase(update.symbol)]: update },
    })),

  addSignal: (signal) => set((s) => ({ signals: mergeSignal(s.signals, signal) })),

  clearSignals: () => set({ signals: [] }),

  initialize: () => {
    if (handlersRegistered) return;
    if (tryRegisterHandlers()) return;
    // Socket not up yet — retry until it is (bounded; cleared by teardown()).
    if (registerRetryTimer !== null) return;
    let attempts = 0;
    registerRetryTimer = setInterval(() => {
      attempts += 1;
      if (tryRegisterHandlers() || attempts >= 60) {
        stopRegisterRetry();
      }
    }, 1000);
  },

  teardown: () => {
    stopRegisterRetry();
    if (!handlersRegistered) return;
    handlersRegistered = false;
    wsService.off('fair-price:update', onFairPriceUpdate);
    wsService.off('fair-price:signal', onFairPriceSignal);
    wsService.off('fair-price:signal-update', onFairPriceSignal);
  },

  fetchState: async (symbol) => {
    try {
      const res = await fetch(
        `${getApiBaseUrl()}/fair-price?symbol=${encodeURIComponent(symbol)}`
      );
      if (!res.ok) {
        warnUnavailableOnce(`GET /fair-price (${res.status})`);
        return;
      }
      const data = await res.json();
      if (!data?.success || !data.state) return;
      set((s) => {
        let signals = s.signals;
        if (Array.isArray(data.signals)) {
          for (const sig of data.signals as FairPriceSignal[]) {
            if (sig && sig.id != null && typeof sig.timestamp === 'number') {
              signals = mergeSignal(signals, sig);
            }
          }
        }
        const fetched = data.state as FairPriceUpdate;
        return {
          state: fetched,
          statesBySymbol: { ...s.statesBySymbol, [normalizeToBase(fetched.symbol)]: fetched },
          signals,
        };
      });
    } catch {
      warnUnavailableOnce('GET /fair-price');
    }
  },

  fetchSettings: async () => {
    try {
      const res = await fetch(`${getApiBaseUrl()}/fair-price-settings`);
      if (!res.ok) {
        warnUnavailableOnce(`GET /fair-price-settings (${res.status})`);
        return;
      }
      const data = await res.json();
      if (data && typeof data === 'object') {
        set((s) => ({ settings: { ...s.settings, ...(data as Partial<FairPriceSettings>) } }));
      }
    } catch {
      warnUnavailableOnce('GET /fair-price-settings');
    }
  },

  updateSettings: async (partial) => {
    // Optimistic local merge so the UI reflects the change immediately.
    const next = { ...get().settings, ...partial };
    set({ settings: next });
    try {
      const res = await fetch(`${getApiBaseUrl()}/fair-price-settings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(next),
      });
      if (!res.ok) {
        warnUnavailableOnce(`POST /fair-price-settings (${res.status})`);
      }
    } catch {
      warnUnavailableOnce('POST /fair-price-settings');
    }
  },
}));

// ============================================================================
// Selector hooks
// ============================================================================
// Scalars / stable references can use plain selectors; ANY selector that
// builds a new array/object is wrapped in useShallow (repo rule).

export const useFairPriceEnabled = (): boolean =>
  useFairPriceStore((s) => s.enabled);

export const useFairPriceState = (): FairPriceUpdate | null =>
  useFairPriceStore((s) => s.state);

/**
 * Engine state for ONE symbol — use this for anything rendered against a
 * specific chart. `useFairPriceState` returns whichever symbol updated last,
 * which puts ES numbers on an MNQ chart.
 */
export const useFairPriceStateForSymbol = (symbol: string): FairPriceUpdate | null =>
  useFairPriceStore((s) => s.statesBySymbol[normalizeToBase(symbol)] ?? null);

export const useFairPriceSettings = (): FairPriceSettings =>
  useFairPriceStore((s) => s.settings);

/** All anchors of the current state ([] when no state yet). */
export const useFairPriceAnchors = (): FairPriceAnchor[] =>
  useFairPriceStore(useShallow((s) => s.state?.anchors ?? []));

/** Only the currently-active anchors. */
export const useActiveFairPriceAnchors = (): FairPriceAnchor[] =>
  useFairPriceStore(useShallow((s) => (s.state?.anchors ?? []).filter((a) => a.active)));

/** Signals filtered to one symbol (exact engine symbol match). */
export const useFairPriceSignalsForSymbol = (symbol: string): FairPriceSignal[] =>
  useFairPriceStore(useShallow((s) => s.signals.filter((x) => x.symbol === symbol)));

/** Count of live (unresolved) signals from the latest update (0 when absent). */
export const useFairPriceLiveSignals = (): number =>
  useFairPriceStore((s) => s.state?.liveSignals ?? 0);

/** Rolling outcome stats from the latest update (null when the engine doesn't send them). */
export const useFairPriceStats = (): FairPriceStats | null =>
  useFairPriceStore(useShallow((s) => s.state?.stats ?? null));
